import {useTranslation} from 'react-i18next';
import {Switcher, Button} from '@tickchak/react-share/app';
import * as css from './LecturerCard.css';
import LecturerCardMenu from './lecturerCardMenu/LecturerCardMenu';
import {teamMemberPhoto} from '../../../../assets/symbols/images/teamMemberPhoto';
import ConferenceBadge from '../../../../assets/icons/ConferenceBadge';
import AccountBadge from '../../../../assets/icons/AccountBadge';
import {TLecturer} from '../../../../redux/timetable/timetable.type';
import {openWhatsApp} from '../../../../library/whatsApp';
import {updateLecturer, deleteLecturer} from '../../../../redux/timetable/timetableActions';
import {bounceMessage} from '../../../../layout/userMessages/UserMessage';
import useDoubleTap from '../../../../hooks/useDoubleTap';

const CONFERENCE_BG = '#FFF4D6';

interface LecturerCardProps {
    lecturer: TLecturer;
    isAssignedToLecture?: boolean;
    onEdit?: () => void;
}

function LecturerCard({lecturer, isAssignedToLecture, onEdit}: LecturerCardProps) {
    const {t} = useTranslation('timetable');
    const dataCy = `lecturer-card-${lecturer.lid}`;
    const active = lecturer.active !== 0 && lecturer.active !== false;

    const handleToggle = (checked: boolean) => {
        if(!checked && (isAssignedToLecture || lecturer.hasAssignedLectures)) {
            const message = lecturer.hasAssignedLectures
                ? t('confirmDeactivateOtherConferencesMessage')
                : t('confirmDeactivateAssignedMessage');
            bounceMessage(
                message,
                'attention',
                {text: t('confirm'), onClick: () => updateLecturer(lecturer.lid, {active: 0})},
                {text: t('cancel')}
            );
            return;
        }
        updateLecturer(lecturer.lid, {active: checked ? 1 : 0});
    };

    const handleDoubleTap = useDoubleTap(onEdit);

    return (
        <css.card data-cy={dataCy} onDoubleClick={onEdit} onTouchEnd={handleDoubleTap}>
            <css.cover>
                <css.topBar>
                    <css.badge $bg={isAssignedToLecture ? CONFERENCE_BG : undefined}>
                        {isAssignedToLecture ? <ConferenceBadge /> : <AccountBadge />}
                        <span>{t(isAssignedToLecture ? 'lecturerInConference' : 'lecturerInAccount')}</span>
                    </css.badge>
                    <css.topBarSide onClick={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
                        <Switcher
                            checked={active}
                            label={{on: t('lecturerActive'), off: t('lecturerInactive'), show: true}}
                            onChange={e => handleToggle(e.target.checked)}
                            data-cy={`${dataCy}-toggle`}
                        />
                        <LecturerCardMenu
                            lecturer={lecturer}
                            onEdit={onEdit}
                            onDelete={() => deleteLecturer(lecturer.lid)}
                            isAssignedToLecture={isAssignedToLecture}
                            onRemoveFromConference={() => updateLecturer(lecturer.lid, {inEvent: false})}
                        />
                    </css.topBarSide>
                </css.topBar>
            </css.cover>
            <css.avatar $image={lecturer.image || teamMemberPhoto} />
            <css.body>
                <css.name dir="auto">{`${lecturer.firstName} ${lecturer.lastName}`.trim()}</css.name>
                <css.footerRow onClick={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
                    <Button
                        variant="primary"
                        size="category"
                        iconStart={<css.whatsAppIcon />}
                        onClick={() => openWhatsApp(lecturer.phone || '', t('lecturerWhatsAppGreeting', {name: `${lecturer.firstName} ${lecturer.lastName}`.trim()}))}
                        ariaLabel={t('lecturerContact')}
                        disabled={!lecturer.phone}
                        data-cy={`${dataCy}-contact`}
                    />
                </css.footerRow>
            </css.body>
        </css.card>
    );
}

export default LecturerCard;
